import { Inject, Injectable, LOCALE_ID } from "@angular/core";
import { Mote, MotesPaths, SupportedLanguage } from "../models";
import { DeviceService } from "./device.service";


@Injectable({ providedIn: 'root' })
export class OpenAppService {

    constructor(private deviceService: DeviceService,
        @Inject(LOCALE_ID) private locale: string) { }

    /**
     * Open native app or App Store/Google Play if uninstalled
     * @param deepLink (optional) app page
     */
    public openApp(deepLink: string = '') {
        if(this.deviceService.isIos()) this.openIosApp(deepLink, null);
        else this.openAndroidApp(deepLink, null);
    }

    /**
     * Start tour in native app or open App Store/Google Play if uninstalled
     * @param tour
     */
    public startTourInApp(deepLink: string, tour: Mote) {
        if(this.deviceService.isIos()) this.openIosApp(deepLink, tour);
        else this.openAndroidApp(deepLink, tour);
    }

    /**
     * Start tour in web app
     * @param tour
     */
    public startTourInWebApp(deepLink: string, tour: Mote) {
        let path = this.getPath(deepLink, tour);
        window.open(`${MotesPaths.MOTES_APP}/${path}#webapp`, '_blank');
    }

    /**
     * Open web app
     * @param deepLink (optional) app page
     */
    public openWebApp(deepLink: string = '') {
        let path = this.getPath(deepLink, null);
        window.open(`${MotesPaths.MOTES_APP}/${path}#webapp`, '_blank');
    }

    private async openIosApp(deepLink: string, tour: Mote): Promise<void> {
        if(!window) return;
        // Open universal link (opens native if installed, otherwise opens web app in new tab)
        let path = this.getPath(deepLink, tour);
        let webAppTab = window.open(`${MotesPaths.MOTES_APP}/${path}`, '_blank');
        // Wait for window response (prevent browser popups)
        await this.sleep(200);
        // If native app opened (no new tab) => do nothing
        if(!webAppTab) return;
        // Otherwise close new tab and open App Store
        webAppTab.close();
        this.openAppStore();
    }

    private async openAndroidApp(deepLink: string, tour: Mote): Promise<void> {
        if(!window) return;
        // Open native app if installed, otherwise open Google Play
        let path = this.getPath(deepLink, tour);
        // Combine native app / url (obs. native syntax is like protocol APP:// - url without prepending /)
        const appUrl = `${MotesPaths.NATIVE_APP_URL}${path}`;
        window.location.replace(appUrl);
        // Wait for native app to open
        await this.sleep(500);
        // Open Google Play (this can only be called if native app has not opened on top of browser)
        this.openGooglePlay(appUrl);
    }

    private sleep(ms: number): Promise<void> {
        return new Promise<void>((resolve) => setTimeout(() => { resolve() }, ms));
    }

    public openAppStoreInWeb() {
        window.open(`https://apps.apple.com/dk/app/storyhunt/id1563214352?l=${this.locale}`, '_blank');
    }

    public openGooglePlayInWeb() {
        window.open(`https://play.google.com/store/apps/details?id=dk.storyhunt.app&pli=1`, '_blank');
    }

    private openAppStore() {
        window.location.href = "itms-apps://apps.apple.com/dk/app/storyhunt/id1563214352";
    }

    public openAppMarketplace() {
        if(this.deviceService.isIos()) this.openAppStore();
        else this.openGooglePlay(null);
    }

    private openGooglePlay(path: string) {
        // Link to play store
        let playUrl = `market://details?id=${MotesPaths.NATIVE_APP_ID}`;
        // Add deferred deeplink to be used after install (if deeplink url provided)
        if(path) playUrl += `&url=${path}`;
        window.location.href = playUrl;
    }

    private getPath(deepLink: string, tour: Mote): string {
        // Add tour language if supported by i18n otherwise add home language
        const lang = (tour && tour.language in SupportedLanguage) ? tour.language : this.locale;
        // Add tour information if applicable
        const tourPath = tour ? `/map/${tour.project}/${tour.key}` : '';
        const deepLinkPath = deepLink || '';

        return `${lang}${tourPath}/${deepLinkPath}`;
    }
}